"use client"

import { useMutation } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { sendTriiboOtp } from "@/api/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const MIN_CELLPHONE_DIGITS = 11
const CELLPHONE_PREFIX = "+55"

const applyCellphoneMask = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 11)

  if (digits.length <= 2) return digits
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

interface CellphoneFormValues {
  cellphone: string
}

interface CellphoneStepFormProps {
  initialCellphone?: string
  onOtpSent: (payload: { cellphone: string; transactionId: string }) => void
}

export const CellphoneStepForm = ({
  initialCellphone = "",
  onOtpSent,
}: CellphoneStepFormProps) => {
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CellphoneFormValues>({
    defaultValues: {
      cellphone: applyCellphoneMask(initialCellphone),
    },
  })

  const cellphone = watch("cellphone")
  const rawDigits = useMemo(() => cellphone.replace(/\D/g, ""), [cellphone])

  const hasValidCellphone = useMemo(() => {
    return rawDigits.length >= MIN_CELLPHONE_DIGITS
  }, [rawDigits])

  const sendOtpMutation = useMutation({
    mutationFn: async (cellphoneValue: string) => {
      return await sendTriiboOtp({
        cellphone: cellphoneValue,
        type: "whatsapp",
      })
    },
  })

  const onSubmit = async (_values: CellphoneFormValues) => {
    setApiError(null) // Clear previous errors
    const fullCellphone = CELLPHONE_PREFIX + rawDigits

    const response = await sendOtpMutation
      .mutateAsync(fullCellphone)
      .catch(() => null)

    if (!response) {
      setApiError("Falha ao enviar codigo via WhatsApp.")
      return
    }

    if (!response.success) {
      setApiError(
        response.message || "Nao foi possivel enviar o codigo via WhatsApp."
      )
      return
    }

    const transactionId = response.success?.transactionId

    if (!transactionId) {
      setApiError("Nao foi possivel obter o ID da transacao.")
      return
    }

    onOtpSent({
      cellphone: fullCellphone,
      transactionId,
    })
  }

  return (
    <form className='space-y-4' onSubmit={handleSubmit(onSubmit)}>
      <div className='space-y-2'>
        <label className='font-medium text-sm' htmlFor='cellphone'>
          Entrar com celular
        </label>
        <div className='relative'>
          <Input
            {...register("cellphone", {
              required: "Insira um numero de celular valido.",
              validate: () => {
                return (
                  hasValidCellphone || "Insira um numero de celular valido."
                )
              },
              onChange: (event) => {
                setValue("cellphone", applyCellphoneMask(event.target.value))
              },
            })}
            autoComplete='tel'
            className='ps-12'
            id='cellphone'
            inputMode='tel'
            name='cellphone'
            placeholder='(XX) XXXXX-XXXX'
            type='tel'
          />
          <span className='pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground text-sm peer-disabled:opacity-50'>
            {CELLPHONE_PREFIX}
          </span>
        </div>

        {errors.cellphone ? (
          <p className='text-red-600 text-sm'>{errors.cellphone.message}</p>
        ) : null}

        {apiError ? <p className='text-red-600 text-sm'>{apiError}</p> : null}
      </div>

      <Button
        className='w-full'
        disabled={sendOtpMutation.isPending}
        type='submit'
      >
        {sendOtpMutation.isPending
          ? "Enviando..."
          : "Solicitar codigo WhatsApp"}
      </Button>
    </form>
  )
}
